/**
 * ProfileCompletion - Modal für Profil-Vervollständigung nach Google Login
 * 
 * Wird angezeigt wenn:
 * - Kunde via Google eingeloggt ist
 * - Mobilnummer oder Geburtsdatum fehlt
 * - AGB nicht akzeptiert wurden
 */
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { PhoneInput } from '@/components/ui/phone-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { portalAuthHeaderValue } from '@/utils/tokenUtils';
import { toast } from 'sonner';
import { User, Phone, Calendar, MapPin, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileCompletion = ({ open, onClose, customer, onProfileUpdated, token: tokenProp, forceComplete = false, prefill = null, onUseExistingAccount = null }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    phone: customer?.phone || '',
    birthdate: customer?.date_of_birth || customer?.birthdate || '',
    street: customer?.street || '',
    zip: customer?.postal_code || customer?.zip || '',
    city: customer?.city || '',
    emergency_contact_name: customer?.emergency_contact?.name || '',
    emergency_contact_phone: customer?.emergency_contact?.phone || ''
  });
  
  const [acceptedTerms, setAcceptedTerms] = useState(customer?.accepted_terms || false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(customer?.accepted_privacy || false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // 08/2026: Notfallkontakt-Pflicht ist per Einstellung schaltbar (Standard optional).
  const [emergencyRequired, setEmergencyRequired] = useState(false);
  // 06/2026: Geburtsdatum & Adresse ebenfalls schaltbar.
  const [addressRequired, setAddressRequired] = useState(false);
  const [birthdateRequired, setBirthdateRequired] = useState(true);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const addrTimer = useRef(null);
  // Phase 4: Doppel-Konto-Erkennung (Name + Geburtsdatum).
  const [dupInfo, setDupInfo] = useState(null);
  const dupTimer = useRef(null);

  // Prüft (debounced), ob bereits ein anderes Konto mit gleichem Namen +
  // Geburtsdatum existiert → Hinweis „Du hast bereits ein Konto".
  useEffect(() => {
    if (!open) return;
    const first = (formData.first_name || '').trim();
    const last = (formData.last_name || '').trim();
    const bd = (formData.birthdate || '').trim();
    if (first.length < 2 || last.length < 2 || !bd) { setDupInfo(null); return; }
    if (dupTimer.current) clearTimeout(dupTimer.current);
    dupTimer.current = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/portal/profile/check-duplicate`,
          { first_name: first, last_name: last, birthdate: bd },
          { headers: { Authorization: portalAuthHeaderValue() } });
        setDupInfo(r.data?.duplicate ? r.data : null);
      } catch { setDupInfo(null); }
    }, 500);
    return () => { if (dupTimer.current) clearTimeout(dupTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.first_name, formData.last_name, formData.birthdate]);

  const onStreetChange = (val) => {
    setFormData((prev) => ({ ...prev, street: val }));
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if ((val || '').trim().length < 3) { setAddrSuggestions([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      setAddrLoading(true); setAddrOpen(true);
      try {
        const r = await axios.get(`${API}/portal/geo/address-search`, {
          params: { q: val }, headers: { Authorization: portalAuthHeaderValue() },
        });
        setAddrSuggestions(r.data?.results || []);
      } catch { setAddrSuggestions([]); }
      finally { setAddrLoading(false); }
    }, 350);
  };

  const pickAddress = (s) => {
    setFormData((prev) => ({ ...prev, street: s.street || prev.street, zip: s.zip || prev.zip, city: s.city || prev.city }));
    setAddrOpen(false); setAddrSuggestions([]);
  };

  useEffect(() => {
    let active = true;
    axios.get(`${API}/portal/settings/public`, { headers: { Authorization: portalAuthHeaderValue() } })
      .then((res) => {
        if (!active) return;
        setEmergencyRequired(!!res.data?.emergency_contact_required);
        setAddressRequired(!!res.data?.address_required);
        setBirthdateRequired(res.data?.birthdate_required !== false);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // 08/2026: Felder neu befüllen wenn Dialog öffnet / Kunde (async) geladen ist
  useEffect(() => {
    if (!open) return;
    const pf = prefill || {};
    setFormData({
      first_name: customer?.first_name || pf.first_name || '',
      last_name: customer?.last_name || pf.last_name || '',
      phone: customer?.phone || '',
      birthdate: customer?.date_of_birth || customer?.birthdate || pf.birthdate || '',
      street: customer?.street || pf.street || '',
      zip: customer?.postal_code || customer?.zip || pf.zip || '',
      city: customer?.city || pf.city || '',
      emergency_contact_name: customer?.emergency_contact?.name || '',
      emergency_contact_phone: customer?.emergency_contact?.phone || ''
    });
    setAcceptedTerms(customer?.accepted_terms || false);
    setAcceptedPrivacy(customer?.accepted_privacy || false);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id, prefill]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name || formData.first_name.trim().length < 2) {
      newErrors.first_name = t('portal.onboarding.err_first_name');
    }
    if (!formData.last_name || formData.last_name.trim().length < 2) {
      newErrors.last_name = t('portal.onboarding.err_last_name');
    }
    
    // Phone is required
    if (!formData.phone || formData.phone.trim().length < 5) {
      newErrors.phone = t('portal.onboarding.err_phone');
    }
    
    // Birthdate — nur Pflicht, wenn in den Einstellungen aktiviert.
    if (birthdateRequired && !formData.birthdate) {
      newErrors.birthdate = t('portal.onboarding.err_birthdate');
    }

    // Adresse — nur Pflicht, wenn in den Einstellungen aktiviert.
    if (addressRequired) {
      if (!formData.street || formData.street.trim().length < 2) {
        newErrors.street = t('portal.onboarding.err_street');
      }
      if (!formData.zip || formData.zip.trim().length < 2) {
        newErrors.zip = t('portal.onboarding.err_zip');
      }
      if (!formData.city || formData.city.trim().length < 2) {
        newErrors.city = t('portal.onboarding.err_city');
      }
    }

    // 08/2026: Notfallkontakt nur Pflicht, wenn in den Einstellungen aktiviert.
    if (emergencyRequired) {
      if (!formData.emergency_contact_name || formData.emergency_contact_name.trim().length < 2) {
        newErrors.emergency_contact_name = t('portal.onboarding.err_emergency_name');
      }
      if (!formData.emergency_contact_phone || formData.emergency_contact_phone.trim().length < 5) {
        newErrors.emergency_contact_phone = t('portal.onboarding.err_emergency_phone');
      }
    }
    
    // Terms must be accepted
    if (!acceptedTerms) {
      newErrors.terms = t('portal.onboarding.err_terms');
    }
    
    // Privacy must be accepted
    if (!acceptedPrivacy) {
      newErrors.privacy = t('portal.onboarding.err_privacy');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      const token = tokenProp || localStorage.getItem('customerToken');
      
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        birthdate: formData.birthdate,
        street: formData.street || null,
        zip: formData.zip || null,
        city: formData.city || null,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_phone: formData.emergency_contact_phone,
        accepted_terms: acceptedTerms,
        accepted_privacy: acceptedPrivacy,
        profile_completed: true,
        // Willkommensnachricht nur senden wenn Profil noch nicht vervollständigt wurde
        send_welcome_notification: !customer?.profile_completed
      };
      
      const response = await axios.put(
        `${API}/portal/profile/complete`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('portal.onboarding.toast_success'));
      
      // Update customer data in parent
      if (onProfileUpdated && response.data.customer) {
        onProfileUpdated(response.data.customer);
      }
      
      onClose();
    } catch (error) {
      console.error('Profile completion error:', error);
      toast.error(error.response?.data?.detail || t('portal.onboarding.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    toast.info(t('portal.onboarding.toast_skip'));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && forceComplete) return; if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white"
        onInteractOutside={(e) => { if (forceComplete) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (forceComplete) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <User className="h-5 w-5 text-yellow-500" />
            {t('portal.onboarding.pc_title')}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {t('portal.onboarding.pc_desc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-gray-300">
                <User className="h-4 w-4" />
                {t('portal.onboarding.first_name')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="profile-first-name"
              />
              {errors.first_name && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.first_name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-gray-300">
                {t('portal.onboarding.last_name')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="profile-last-name"
              />
              {errors.last_name && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.last_name}
                </p>
              )}
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-300">
              <Phone className="h-4 w-4" />
              {t('portal.onboarding.phone')} <span className="text-red-500">*</span>
            </Label>
            <PhoneInput
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              isDark={true}
            />
            {errors.phone && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.phone}
              </p>
            )}
          </div>
          
          {/* Birthdate */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-300">
              <Calendar className="h-4 w-4" />
              {t('portal.onboarding.birthdate')} {birthdateRequired
                ? <span className="text-red-500">*</span>
                : <span className="text-white/70 text-sm">{t('portal.onboarding.optional')}</span>}
            </Label>
            <Input
              type="date"
              value={formData.birthdate}
              onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
              className="bg-gray-800 border-gray-600 text-white"
            />
            {errors.birthdate && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.birthdate}
              </p>
            )}
          </div>
          
          {/* Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-300">
              <MapPin className="h-4 w-4" />
              {t('portal.onboarding.address')} {addressRequired
                ? <span className="text-red-500">*</span>
                : <span className="text-white/70 text-sm">{t('portal.onboarding.optional')}</span>}
            </Label>
            <div className="relative">
              <Input
                type="text"
                placeholder={t('portal.onboarding.ph_street')}
                value={formData.street}
                onChange={(e) => onStreetChange(e.target.value)}
                onFocus={() => { if (addrSuggestions.length) setAddrOpen(true); }}
                onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
                autoComplete="off"
                data-testid="profile-street"
                className="bg-gray-800 border-gray-600 text-white placeholder:text-white/70"
              />
              {addrOpen && (
                <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-700 bg-gray-900 shadow-xl" data-testid="profile-address-suggestions">
                  {addrLoading && <div className="px-3 py-2 text-sm text-white/60 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> {t('common.loading', 'Suche…')}</div>}
                  {!addrLoading && addrSuggestions.length === 0 && <div className="px-3 py-2 text-sm text-white/60">—</div>}
                  {!addrLoading && addrSuggestions.map((s, i) => (
                    <button type="button" key={i} onMouseDown={(e) => { e.preventDefault(); pickAddress(s); }}
                      data-testid={`profile-address-suggestion-${i}`}
                      className="block w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-800 border-b border-gray-800 last:border-0">
                      <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-amber-400 shrink-0" />{s.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errors.street && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.street}
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="text"
                placeholder={t('portal.onboarding.ph_zip')}
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                data-testid="profile-zip"
                className="bg-gray-800 border-gray-600 text-white placeholder:text-white/70"
              />
              <Input
                type="text"
                placeholder={t('portal.onboarding.ph_city')}
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                data-testid="profile-city"
                className="col-span-2 bg-gray-800 border-gray-600 text-white placeholder:text-white/70"
              />
            </div>
            {(errors.zip || errors.city) && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.zip || errors.city}
              </p>
            )}
          </div>

          {/* 08/2026: Notfallkontakt (Pflicht) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-300">
              <Phone className="h-4 w-4" />
              {t('portal.onboarding.emergency')} {emergencyRequired
                ? <span className="text-red-500">*</span>
                : <span className="text-xs font-normal text-gray-400">{t('portal.onboarding.emergency_optional')}</span>}
            </Label>
            <Input
              type="text"
              placeholder={t('portal.onboarding.ph_emergency_name')}
              value={formData.emergency_contact_name}
              onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
              className="bg-gray-800 border-gray-600 text-white placeholder:text-white/70"
              data-testid="profile-emergency-name"
            />
            {errors.emergency_contact_name && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.emergency_contact_name}
              </p>
            )}
            <PhoneInput
              value={formData.emergency_contact_phone}
              onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
              isDark={true}
              data-testid="profile-emergency-phone"
            />
            {errors.emergency_contact_phone && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.emergency_contact_phone}
              </p>
            )}
          </div>
          
          {/* Divider */}
          <div className="border-t border-gray-700 pt-4">
            {/* Terms */}
            <div className="flex items-start space-x-3 mb-3">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                className="border-gray-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500 mt-1"
              />
              <div>
                <Label htmlFor="terms" className="text-gray-300 cursor-pointer">
                  {t('portal.onboarding.i_accept')} <a href="/agb" target="_blank" className="text-yellow-500 hover:underline">{t('portal.onboarding.terms_link')}</a> <span className="text-red-500">*</span>
                </Label>
                {errors.terms && (
                  <p className="text-red-400 text-sm flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" /> {errors.terms}
                  </p>
                )}
              </div>
            </div>
            
            {/* Privacy */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="privacy"
                checked={acceptedPrivacy}
                onCheckedChange={setAcceptedPrivacy}
                className="border-gray-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500 mt-1"
              />
              <div>
                <Label htmlFor="privacy" className="text-gray-300 cursor-pointer">
                  {t('portal.onboarding.i_accept')} <a href="/datenschutz" target="_blank" className="text-yellow-500 hover:underline">{t('portal.onboarding.privacy_link')}</a> <span className="text-red-500">*</span>
                </Label>
                {errors.privacy && (
                  <p className="text-red-400 text-sm flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" /> {errors.privacy}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {dupInfo && (
          <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 space-y-3" data-testid="profile-duplicate-warning">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100/90">
                <p className="font-semibold text-amber-200">
                  {t('portal.onboarding.dup_title', 'Du hast bereits ein Konto bei uns')}
                </p>
                <p className="mt-1">
                  {t('portal.onboarding.dup_desc', 'Mit diesem Namen und Geburtsdatum existiert bereits ein Konto')}
                  {dupInfo.masked_email ? ` (${dupInfo.masked_email})` : ''}.
                  {' '}
                  {t('portal.onboarding.dup_hint', 'Bitte melde dich mit deinem bestehenden Konto an – deine Verordnung wird dann dort gebucht.')}
                </p>
              </div>
            </div>
            {onUseExistingAccount && (
              <Button
                onClick={onUseExistingAccount}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="profile-duplicate-login-btn"
              >
                {t('portal.onboarding.dup_login', 'Mit bestehendem Konto anmelden')}
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          {!forceComplete && (
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-white/60 hover:text-white hover:bg-gray-800"
            >
              {t('portal.onboarding.later')}
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('portal.onboarding.saving')}
              </>
            ) : (
              t('portal.onboarding.save_profile')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletion;
